import React, { useMemo } from 'react';
import clsx from 'clsx';
import { MessageDescriptor, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { StreamingSectionKey } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import {
  HIDDEN_WHEN_EMPTY_CHIPS,
  SOURCE_SECTION_CHIPS,
  STREAMING_CHIP_SECTIONS,
  StreamingChipSection,
  StreamingSection,
  streamingSectionPath,
} from '../streamingPaths';
import { streamingMessages as messages } from '../streamingMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

const CHIP_LABELS: Record<StreamingChipSection, MessageDescriptor> = {
  overview: messages.chipOverview,
  tracks: messages.chipTracks,
  stores: messages.chipStores,
  countries: messages.chipCountries,
  audience: messages.chipAudience,
  playSources: messages.chipPlaySources,
  shortVideo: messages.chipShortVideo,
  accelerator: messages.chipAccelerator,
  playlists: messages.chipPlaylists,
  earnings: messages.chipEarnings,
};

const NO_SECTIONS: readonly StreamingSectionKey[] = [];

interface StreamingSectionChipsProps {
  section: StreamingSection;
  days: number;
  /**
   * The sections that no connected source gives: their chips are dashed. Nothing is dashed only
   * because no distributor is connected (owner decision 2026-10-06).
   */
  emptySections: readonly StreamingSectionKey[];
  /** True: the Earnings chip has a lock (design e2). */
  earningsHidden: boolean;
  /** The sections that the plan does not show (`sections.<key>.locked: 'plan'`): their chips have a lock. */
  lockedSections?: readonly StreamingSectionKey[];
  onOpen: (path: string) => void;
}

/**
 * The section chips under the title (design b1, streaming-sections e1). The track detail lights
 * "Tracks". A chip of a section that no connected source gives is dashed; Accelerator is not drawn.
 */
const StreamingSectionChipsComponent: React.FC<StreamingSectionChipsProps> = ({
  section,
  days,
  emptySections,
  earningsHidden,
  lockedSections = NO_SECTIONS,
  onOpen,
}) => {
  const intl = useIntl();
  const current = section === 'track' ? 'tracks' : section;
  const chips = useMemo(
    () =>
      STREAMING_CHIP_SECTIONS.map((chip) => {
        const sourceSection = SOURCE_SECTION_CHIPS[chip];
        const isEmpty = sourceSection !== undefined && emptySections.includes(sourceSection);
        // Accelerator is not drawn without a source that gives it (design streaming-sections e1).
        // The open section keeps its chip.
        if (isEmpty && HIDDEN_WHEN_EMPTY_CHIPS.includes(chip) && chip !== current) {
          return null;
        }
        const isDashed = isEmpty;
        const isLocked =
          (chip === 'earnings' && earningsHidden) ||
          (sourceSection !== undefined && lockedSections.includes(sourceSection));
        return (
          <button
            key={chip}
            type="button"
            aria-current={chip === current ? 'page' : undefined}
            data-dashed={isDashed ? 'true' : undefined}
            className={clsx(styles.chip, chip === current && styles.chipOn, isDashed && styles.chipDashed)}
            onClick={() => onOpen(streamingSectionPath(chip, days))}
          >
            {isLocked ? <FontAwesomeIcon icon={faLock} data-testid={`streaming-${chip}-lock`} /> : null}
            {intl.formatMessage(CHIP_LABELS[chip])}
          </button>
        );
      }),
    [current, days, earningsHidden, emptySections, intl, lockedSections, onOpen]
  );

  return (
    <nav className={styles.chips} aria-label={intl.formatMessage(messages.chipsLabel)}>
      {chips}
    </nav>
  );
};

export const StreamingSectionChips = TypedMemo(StreamingSectionChipsComponent);
